// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AppCoverArt } from '@/components/greenlight/AppCoverArt';
import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { pipelineMeta, statusMeta } from '@/components/greenlight/appStatusMeta';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { OwnerAvatar } from '@/components/greenlight/OwnerAvatar';
import { pipelineIcon } from '@/components/greenlight/pipelineIcon';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { type AppListItem } from '@greenlight/shared';

interface AppGridCardProps {
  app: AppListItem;
}

// Screenshot cover: the app's captured cover, or the branded cover art when
// none exists yet (docs/28 §Imagery — intentional identity art, never an empty
// frame). aria-hidden: the card's name is the accessible label for the whole
// card link.
function Cover({ app }: { app: AppListItem }): React.JSX.Element {
  const url = app.cover_screenshot_url;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  const showImage = url != null && url !== '' && !failed;

  return (
    <div className="relative aspect-[16/10] w-full overflow-hidden border-b border-border bg-muted/40">
      {showImage ? (
        <img
          src={url}
          alt=""
          aria-hidden
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <AppCoverArt name={app.name} slug={app.slug} iconUrl={app.icon_url} />
      )}
    </div>
  );
}

/**
 * Grid / app-store card for the /apps Grid view mode (docs/28 §/apps View
 * modes). Screenshot-forward: a cover image (or placeholder) over the app's
 * identity, status, owner, last deploy, and last pipeline. The whole card is a
 * single link to the detail page (matching the list's row-activate), with a
 * Manage affordance mirroring the list row action.
 */
export function AppGridCard({ app }: AppGridCardProps): React.JSX.Element {
  const meta = statusMeta(app.status);
  const pMeta = app.last_pipeline_status ? pipelineMeta(app.last_pipeline_status) : null;
  const pIcon = pMeta ? pipelineIcon(pMeta.variant) : null;

  return (
    <Card className="group overflow-hidden border-border/60 p-0 transition-colors hover:border-border">
      <Link
        to={`/apps/${app.app_id}`}
        aria-label={`Manage ${app.name}`}
        className="flex h-full flex-col rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <Cover app={app} />
        <div className="flex flex-1 flex-col gap-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <AppIconTile iconUrl={app.icon_url} name={app.name} slug={app.slug} size={36} />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="flex items-center gap-1.5 truncate text-[15px] font-semibold text-foreground">
                  <span className="truncate">{app.name}</span>
                </span>
                <MonoValue className="w-fit text-[11px]">
                  {app.serving_subdomain ?? app.slug}
                </MonoValue>
              </div>
            </div>
            <StatusBadge variant={meta.variant} className="mt-0.5 shrink-0">
              {meta.label}
            </StatusBadge>
          </div>

          {app.owner_email && (
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <OwnerAvatar name={app.owner_name} email={app.owner_email} />
              <span className="min-w-0 truncate text-sm text-muted-foreground">
                {app.owner_email}
              </span>
            </div>
          )}

          {(app.last_deploy_at || pIcon) && (
            <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
              {app.last_deploy_at && <span>Deployed {relativeTime(app.last_deploy_at)}</span>}
              {pIcon && pMeta && (
                <span
                  className={cn('inline-flex items-center', pIcon.className)}
                  title={`Last pipeline: ${pMeta.label}`}
                >
                  <pIcon.Icon
                    className={cn('size-4', pIcon.spin && 'motion-safe:animate-spin')}
                    role="img"
                    aria-label={`Last pipeline: ${pMeta.label}`}
                  />
                </span>
              )}
            </div>
          )}

          <div className="mt-auto flex justify-end pt-1">
            <span className="inline-flex h-9 items-center rounded-gl border border-border px-3 text-[13px] font-medium text-foreground transition-colors group-hover:bg-accent">
              Manage
            </span>
          </div>
        </div>
      </Link>
    </Card>
  );
}
